import {
    ProductVariantsResponse,
    type SubscriptionAddress,
    SubscriptionItem,
    SubscriptionItemSubscription,
    type SubscriptionOrder,
    type SubscriptionOrdersResponse,
} from '@libs/common-next';
import { getAccessToken, handleApiFetchResponse, ResponseWithParsedData } from '@libs/core';

import { cweaClient } from '../clients/api-proxy-browser-client';
import { CWEA_ENDPOINTS } from '../constants';

type SubscriptionApiResponse<T> = T | Error | ResponseWithParsedData<T>;

export interface SetPlaceDateRequest {
    place: string;
}

export interface SetPlaceDateResponse {
    id: string;
    place: string;
}

const assertAccessTokenOrThrow = (action: string): void => {
    const accessToken = getAccessToken();
    if (!accessToken) {
        throw new Error(`Access token is required to ${action}`);
    }
};

/**
 * Request body for reactivating a subscription
 */
export interface ReactivateSubscriptionRequestBody {
    startDate: string;
    every: number;
    everyPeriod: number;
}

/**
 * Fetches subscription orders from the API
 * @returns Promise with subscription orders response
 * @throws Error if the customer UUID is not available, the request fails, or returns null
 */
export async function getSubscriptionOrders(): Promise<SubscriptionApiResponse<SubscriptionOrdersResponse>> {
    const url = CWEA_ENDPOINTS.subscriptions.getOrders();

    assertAccessTokenOrThrow('fetch subscription orders');

    const response = await cweaClient.get<SubscriptionOrdersResponse>(url);

    if (response === null) {
        throw new Error('Failed to fetch subscription orders');
    }
    return response;
}

export async function getSubscriptionOrder(subscriptionId: string): Promise<SubscriptionOrder> {
    const url = CWEA_ENDPOINTS.subscriptions.getSubscriptionOrder(subscriptionId);

    assertAccessTokenOrThrow('fetch subscription order by subscription id');

    const response = await cweaClient.get<SubscriptionApiResponse<SubscriptionOrder>>(url);

    if (response === null) {
        throw new Error('Failed to fetch subscription order by subscription id');
    }

    if (response instanceof Error) {
        throw response;
    }

    return response as SubscriptionOrder;
}

type SubscriptionShippingAddressResponse = SubscriptionAddress | { address: SubscriptionAddress };

const hasAddressShape = (value: unknown): value is SubscriptionAddress =>
    Boolean(
        value &&
            typeof value === 'object' &&
            'line1' in value &&
            'city' in value &&
            'stateProvince' in value &&
            'postalCode' in value
    );

const resolveSubscriptionShippingAddress = (
    response: SubscriptionShippingAddressResponse
): SubscriptionAddress | null => {
    if (hasAddressShape(response)) {
        return response;
    }

    if (
        response &&
        typeof response === 'object' &&
        'address' in response &&
        hasAddressShape((response as { address: unknown }).address)
    ) {
        return (response as { address: SubscriptionAddress }).address;
    }

    return null;
};

export async function getSubscriptionShippingAddress(subscriptionId: string): Promise<SubscriptionAddress> {
    const url = CWEA_ENDPOINTS.subscriptions.getSubscriptionShippingAddress(subscriptionId);

    assertAccessTokenOrThrow('fetch subscription shipping address by subscription id');

    const response = await cweaClient.get<SubscriptionApiResponse<SubscriptionShippingAddressResponse>>(url);

    if (response === null) {
        throw new Error('Failed to fetch subscription shipping address by subscription id');
    }

    if (response instanceof Error) {
        throw response;
    }

    const shippingAddress = resolveSubscriptionShippingAddress(response as SubscriptionShippingAddressResponse);
    if (!shippingAddress) {
        throw new Error('Failed to parse subscription shipping address by subscription id');
    }

    return shippingAddress;
}

export async function getOrderItems(orderId: string): Promise<SubscriptionItem[]> {
    const url = CWEA_ENDPOINTS.subscriptions.getOrderItems(orderId);

    assertAccessTokenOrThrow('fetch order items');

    const response = await cweaClient.get<SubscriptionItem[]>(url);

    const result = handleApiFetchResponse(response);

    if (result === null) {
        throw new Error('Failed to fetch order items');
    }

    return result;
}

/**
 * Fetches product variants from the API
 * @returns Promise with products array response
 * @param orderId - The order ID
 * @param masterProductKey - The master product key
 * @throws Error if the customer UUID is not available, the request fails, or returns null
 */
export async function getProductVariants(
    masterProductKey: string
): Promise<SubscriptionApiResponse<ProductVariantsResponse[]>> {
    const baseUrl = CWEA_ENDPOINTS.subscriptions.getProductVariants();
    const url = `${baseUrl}?group=${masterProductKey}`;

    assertAccessTokenOrThrow('fetch product variants');

    const response = await cweaClient.get<ProductVariantsResponse[]>(url);

    if (response === null) {
        throw new Error('Failed to fetch product variants');
    }
    return response;
}

export async function updateProductQuantity(
    productId: string,
    quantity: string,
    isOneTime: boolean = false
): Promise<SubscriptionApiResponse<ProductVariantsResponse[]>> {
    const url = isOneTime
        ? CWEA_ENDPOINTS.subscriptions.updateOneTimeProductQuantity(productId)
        : CWEA_ENDPOINTS.subscriptions.updateProductQuantity(productId);

    assertAccessTokenOrThrow('update product quantity');

    const response = await cweaClient.patch<ProductVariantsResponse[], { quantity: string }>(url, {
        body: {
            quantity: quantity,
        },
    });

    if (response === null) {
        throw new Error('Failed to update product quantity');
    }
    return response;
}

export async function updateProduct(
    subscriptionId: string,
    newProductId: string
): Promise<SubscriptionApiResponse<ProductVariantsResponse[]>> {
    const url = CWEA_ENDPOINTS.subscriptions.updateProduct(subscriptionId);

    assertAccessTokenOrThrow('update product');

    const response = await cweaClient.patch<ProductVariantsResponse[], { product: string }>(url, {
        body: {
            product: newProductId,
        },
    });

    if (response === null) {
        throw new Error('Failed to update product');
    }
    return response;
}

export async function updateFrequency(
    subscriptionId: string,
    every: string,
    everyPeriod: string
): Promise<SubscriptionApiResponse<ProductVariantsResponse[]>> {
    const url = CWEA_ENDPOINTS.subscriptions.updateFrequency(subscriptionId);

    assertAccessTokenOrThrow('update frequency');

    const response = await cweaClient.patch<ProductVariantsResponse[], { every: string; everyPeriod: string }>(url, {
        body: {
            every: every,
            everyPeriod: everyPeriod,
        },
    });

    if (response === null) {
        throw new Error('Failed to update frequency');
    }
    return response;
}

/**
/**
* Cancels a subscription
 * @param subscriptionId - The subscription ID to cancel
 * @param cancelReason - Optional cancel reason in format "{code} | {reason text}"
 * @returns Promise with subscription orders response
 * @throws Error if the customer UUID is not available, the request fails, or returns null
 */
export async function cancelSubscription(
    subscriptionId: string,
    cancelReason?: string
): Promise<SubscriptionApiResponse<SubscriptionOrdersResponse>> {
    const url = CWEA_ENDPOINTS.subscriptions.cancelSubscription(subscriptionId);

    assertAccessTokenOrThrow('cancel subscription');

    const response = await cweaClient.patch<SubscriptionOrdersResponse, { cancelReason?: string }>(url, {
        body: {
            ...(cancelReason && { cancelReason }),
        },
    });

    if (response === null) {
        throw new Error('Failed to cancel subscription');
    }
    return response;
}
/**
 * Updates the place date for a subscription order
 * @param orderId - The subscription order ID
 * @param placeDate - The new place date in ISO format (YYYY-MM-DD)
 * @returns Promise with the updated order response
 * @throws Error if the customer UUID is not available, the request fails, or returns null
 */
const getSetPlaceDateErrorMessage = (parsed: unknown): string => {
    if (
        parsed &&
        typeof parsed === 'object' &&
        'detail' in parsed &&
        typeof (parsed as { detail: unknown }).detail === 'string'
    ) {
        return (parsed as { detail: string }).detail;
    }
    return 'Failed to update subscription place date';
};

export async function setPlaceDate(orderId: string, placeDate: string): Promise<SetPlaceDateResponse> {
    try {
        const url = CWEA_ENDPOINTS.subscriptions.setPlaceDate(orderId);

        assertAccessTokenOrThrow('update subscription place date');

        const response = await cweaClient.patch<SetPlaceDateResponse, SetPlaceDateRequest>(url, {
            body: {
                place: placeDate,
            },
        });

        if (response === null) {
            throw new Error('Failed to update subscription place date');
        }

        if (response instanceof Error) {
            throw response;
        }

        if (response instanceof Response) {
            const parsed = (response as ResponseWithParsedData<unknown>).parsedResponse;
            throw new Error(getSetPlaceDateErrorMessage(parsed));
        }

        if (
            typeof response === 'object' &&
            response !== null &&
            'status' in response &&
            typeof (response as { status: unknown }).status === 'number' &&
            (response as { status: number }).status >= 400
        ) {
            throw new Error(getSetPlaceDateErrorMessage(response));
        }

        if ('parsedResponse' in response) {
            return (response as { parsedResponse: SetPlaceDateResponse }).parsedResponse;
        }

        return response;
    } catch (error) {
        throw new Error(`Failed to change order date: ${error}`);
    }
}

export async function cancelOrder(
    orderId: string
): Promise<SubscriptionOrder | Error | ResponseWithParsedData<SubscriptionOrder>> {
    const url = CWEA_ENDPOINTS.subscriptions.cancelOrder(orderId);

    const response = await cweaClient.patch<SubscriptionOrder>(url, {
        headers: {
            'content-type': 'application/json',
        },
        body: JSON.stringify({}),
    });

    if (response === null) {
        throw new Error('Failed to cancel subscription');
    }
    return response;
}
/**
 * Sends an autoship order immediately
 * @param orderId - The order ID to send
 * @param _sourceId - The source ID (e.g., '2801' for US, '2802' for CA) - automatically added by Akamai, kept for backward compatibility
 * @returns Promise with updated subscription order
 * @throws Error if the customer UUID is not available, the request fails, or returns null
 */
export async function sendNowOrder(orderId: string): Promise<SubscriptionOrder> {
    try {
        const url = CWEA_ENDPOINTS.subscriptions.sendNow(orderId);

        assertAccessTokenOrThrow('send order');

        const response = await cweaClient.patch<SubscriptionOrder, Record<string, never>>(url, {
            body: {},
        });

        if (response === null) {
            throw new Error('Failed to send order');
        }

        if (response instanceof Error) {
            throw response;
        }

        if (response instanceof Response || ('status' in response && response.status === 400)) {
            throw new Error('Failed to send order');
        }

        return response;
    } catch (error) {
        throw new Error(`Failed to send order: ${error}`);
    }
}

/**
 * Reactivates a subscription
 * @param subscriptionId - The subscription ID to reactivate
 * @param body - The reactivation request body containing startDate, every, and everyPeriod
 * @returns Promise with the reactivated subscription response
 * @throws Error if the customer UUID is not available, the request fails, or returns null
 */
export async function reactivateSubscription(
    subscriptionId: string,
    body: ReactivateSubscriptionRequestBody
): Promise<SubscriptionItemSubscription> {
    const url = CWEA_ENDPOINTS.subscriptions.reactivate(subscriptionId);

    assertAccessTokenOrThrow('reactivate subscription');

    const response = await cweaClient.patch<SubscriptionItemSubscription, ReactivateSubscriptionRequestBody>(url, {
        body,
    });

    const result = handleApiFetchResponse(response);

    if (result === null) {
        throw new Error('Failed to reactivate subscription');
    }

    return result;
}

/**
 * Request body for updating shipping address
 */
export interface UpdateShippingAddressRequest {
    firstName: string;
    lastName: string;
    label?: string;
    companyName?: string;
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
    phone: string;
    fax?: string;
    tokenId?: string;
    addressId?: string;
    associatedSubscriptions?: string[];
    associatedOrders?: string[];
}

export function toPodsAddressId(addressId: unknown): string | undefined {
    if (typeof addressId !== 'number' && typeof addressId !== 'string') {
        return undefined;
    }

    const id = typeof addressId === 'string' && addressId.trim() === '' ? Number.NaN : Number(addressId);

    return Number.isInteger(id) && id > 0 ? String(id) : undefined;
}

export function getPodsAddressIdFromResponse(response: unknown): string | undefined {
    if (typeof response !== 'object' || response === null || !('addressId' in response)) {
        return undefined;
    }

    return toPodsAddressId(response.addressId);
}

export function withPodsAddressId(addressId: unknown): { addressId?: string } {
    const podsAddressId = toPodsAddressId(addressId);

    return podsAddressId ? { addressId: podsAddressId } : {};
}

/**
 * Response for updating shipping address
 */
export interface UpdateShippingAddressResponse {
    items: Array<Record<string, unknown>>;
}

/**
 * Updates the shipping address for a subscription order
 * @param ordersIds
 * @param subscriptionsIds
 * @param addressData - The shipping address data
 * @returns Promise with the updated order response
 * @throws Error if the customer UUID is not available, the request fails, or returns null
 */
export async function updateShippingAddress(
    ordersIds: string[],
    subscriptionsIds: string[],
    addressData: UpdateShippingAddressRequest
): Promise<UpdateShippingAddressResponse> {
    try {
        const url = CWEA_ENDPOINTS.subscriptions.updateShippingAddressForOrdersAndSubscriptions();

        assertAccessTokenOrThrow('update shipping address');

        const response = await cweaClient.patch<UpdateShippingAddressResponse, UpdateShippingAddressRequest>(url, {
            body: {
                ...addressData,
                associatedOrders: ordersIds,
                associatedSubscriptions: subscriptionsIds,
            },
        });

        if (response === null) {
            throw new Error('Failed to update shipping address');
        }

        if (response instanceof Error) {
            throw response;
        }

        if ('parsedResponse' in response) {
            return response.parsedResponse;
        }

        return response;
    } catch (error) {
        throw new Error(`Failed to update shipping address: ${error}`);
    }
}

/**
 * Updates the shipping address for all subscription orders
 * @param addressData - The shipping address data
 * @returns Promise with the updated response
 * @throws Error if the customer UUID is not available, the request fails, or returns null
 */
export async function updateShippingAddressForAll(
    addressData: UpdateShippingAddressRequest
): Promise<UpdateShippingAddressResponse> {
    const url = `${CWEA_ENDPOINTS.subscriptions.updateShippingAddressForAll()}`;

    assertAccessTokenOrThrow('update shipping address for all');

    const response = await cweaClient.post<UpdateShippingAddressResponse, UpdateShippingAddressRequest>(url, {
        body: addressData,
    });

    if (response === null) {
        throw new Error('Failed to update shipping address for all');
    }

    if (response instanceof Error) {
        throw response;
    }

    if ('parsedResponse' in response) {
        return response.parsedResponse;
    }

    return response;
}

/**
 * Skips an item from the next subscription shipment
 * @param itemId - The item ID to skip
 * @returns Promise that resolves when the item is skipped
 * @throws Error if the customer UUID is not available or the request fails
 */
export async function skipItem(itemId: string): Promise<void> {
    const url = CWEA_ENDPOINTS.subscriptions.skipItem(itemId);

    assertAccessTokenOrThrow('skip item');

    const response = await cweaClient.delete(url);

    // For DELETE requests, we only need to check if there was an error
    // Success returns 204 No Content which handleApiFetchResponse converts to null
    if (response instanceof Error) {
        throw response;
    }

    if (response instanceof Response && !response.ok) {
        throw new Error('Failed to skip item');
    }
}
